import { toJpeg } from 'html-to-image';

/**
 * A picture of the poster exactly as this browser drew it, taken when the customer adds to cart.
 * It is kept beside the order so the admin can check the backend's print file against what the
 * customer actually had on screen. Best effort: it must never get in the way of ordering.
 *
 * The map is a WebGL canvas, and reading one of those during a DOM capture is unreliable in some
 * browsers (an empty frame comes back). So the map is snapshotted separately, on a freshly drawn
 * frame once tiles have settled, and that image is laid over the canvas for the capture.
 */
/** The few Mapbox map methods the capture needs (kept loose so this file does not pull in Mapbox's types). */
interface MapLike {
  loaded(): boolean;
  once(event: string, fn: () => void): unknown;
  off(event: string, fn: () => void): unknown;
  triggerRepaint(): void;
  getCanvas(): HTMLCanvasElement;
}

export async function capturePoster(): Promise<string | null> {
  const node = document.querySelector<HTMLElement>('.tmd-stage [data-poster]');
  if (!node) return null;
  // Fonts still loading would be captured as fallbacks.
  await document.fonts?.ready.catch(() => {});
  const gl = node.querySelector<HTMLDivElement & { __tmMap?: MapLike }>('.tmp-gl');
  const map = gl?.__tmMap;
  // Hide editor-only decoration (empty-field hints, the selection outline) for the capture.
  node.classList.add('is-capturing');
  let overlay: HTMLImageElement | null = null;
  try {
    if (map && gl) {
      const snapshot = await snapshotMap(map);
      if (snapshot) {
        overlay = document.createElement('img');
        overlay.src = snapshot;
        overlay.alt = '';
        Object.assign(overlay.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', pointerEvents: 'none' });
        gl.appendChild(overlay);
        await overlay.decode().catch(() => {});
      }
    }
    const fontEmbedCSS = (await posterFontCSS()) ?? undefined;
    const capture = toJpeg(node, { pixelRatio: 2, quality: 0.9, width: node.offsetWidth, height: node.offsetHeight, fontEmbedCSS });
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 12000));
    return await Promise.race([capture, timeout]);
  } catch {
    return null;
  } finally {
    overlay?.remove();
    node.classList.remove('is-capturing');
  }
}

/**
 * The poster's @font-face rules with the fonts inlined, for html-to-image to embed.
 *
 * html-to-image finds fonts by reading each stylesheet's rules, which the browser refuses for a
 * cross-origin sheet loaded without `crossorigin` (the theme loads ours from the designer's host).
 * It then silently captures the fallback font, which is wider and gets clipped. So the rules are
 * read here instead, fetching the sheet's text when its rules are off limits (the host allows CORS).
 * Null when nothing is found, which leaves html-to-image to try on its own.
 */
let fontCSS: Promise<string | null> | null = null;
const posterFontCSS = () => (fontCSS ??= loadPosterFontCSS().catch(() => null));

async function loadPosterFontCSS(): Promise<string | null> {
  const sheets = await Promise.all(
    [...document.styleSheets].map(async (sheet) => {
      try {
        return { text: [...sheet.cssRules].map((r) => r.cssText).join('\n'), base: sheet.href ?? location.href };
      } catch {
        if (!sheet.href) return null;
        const res = await fetch(sheet.href).catch(() => null);
        return res?.ok ? { text: await res.text(), base: sheet.href } : null;
      }
    }),
  );
  const rules: Promise<string>[] = [];
  for (const sheet of sheets) {
    for (const rule of sheet?.text.match(/@font-face\s*{[^}]*}/g) ?? []) {
      if (!/font-family:\s*["']?DIN/i.test(rule)) continue;
      const src = /url\(\s*["']?([^"')]+)["']?\s*\)/.exec(rule)?.[1];
      if (!src) continue;
      rules.push(
        fetch(new URL(src, sheet!.base))
          .then((r) => (r.ok ? r.blob() : Promise.reject()))
          .then(dataUrl)
          .then((data) => rule.replace(/url\([^)]*\)/g, `url("${data}")`)),
      );
    }
  }
  const css = (await Promise.allSettled(rules)).flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
  return css.length ? css.join('\n') : null;
}

const dataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/** The map's pixels from a frame drawn just now, after giving tiles a moment to finish loading. */
async function snapshotMap(map: MapLike): Promise<string | null> {
  if (!map.loaded()) {
    await new Promise<void>((resolve) => {
      const done = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        map.off('idle', done);
        resolve();
      }, 2500);
      map.once('idle', done);
    });
  }
  return new Promise<string | null>((resolve) => {
    const timer = setTimeout(() => resolve(null), 1500);
    map.once('render', () => {
      clearTimeout(timer);
      try {
        resolve(map.getCanvas().toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    });
    map.triggerRepaint();
  });
}
